/* eslint-disable react/prop-types */
/**
 * RiskSummaryPanel — the board-ready per-risk summary (breadcrumb chips → signpost-alert banner →
 * narrative → Best / Trending / Worst × Value / Preparedness / Velocity table). Lifted from the
 * radar's risk pop-up so the standalone risk view and the radar show the same thing. Fed a shaped
 * risk from GET /emergingRisk/radar-data (scenarioRows + signpostProximity), so the numbers match
 * exactly. Pure presentation — no fetching.
 */
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import Tooltip from "@mui/material/Tooltip";
import Table from "@mui/material/Table";
import TableHead from "@mui/material/TableHead";
import TableBody from "@mui/material/TableBody";
import TableRow from "@mui/material/TableRow";
import TableCell from "@mui/material/TableCell";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";

const fmtMoney = (v) => {
  const n = Number(v || 0);
  if (!n) return "—";
  const abs = Math.abs(n);
  if (abs >= 1e9) return `$${(n / 1e9).toFixed(1)}B`;
  if (abs >= 1e6) return `$${(n / 1e6).toFixed(1)}M`;
  if (abs >= 1e3) return `$${(n / 1e3).toFixed(0)}k`;
  return `$${n.toFixed(0)}`;
};

const TH = { fontWeight: 700, fontSize: "0.78rem", color: "#7b809a", py: 0.85 };
const TD = { fontSize: "0.85rem", py: 0.85 };

function ValueCell({ sc }) {
  const qualText = `${sc.consequenceType || ""} ${sc.qualitative || ""}`.trim();
  // ID196 — assessment is qualitative: always show the consequence "<Type> Level" (e.g. "Brand and
  // Reputation Level 2"), not the $ figure. Fall back to $ only when no qualitative level is set.
  const showQualText = !!qualText;
  const tip = (
    <Box sx={{ fontSize: "0.78rem", lineHeight: 1.5 }}>
      <div>Consequence: {qualText || "—"}</div>
      {sc.quant > 0 ? <div>Quantitative estimate: {fmtMoney(sc.quant)}</div> : null}
    </Box>
  );
  return (
    <Box sx={{ display: "inline-flex", alignItems: "center", gap: 0.3 }}>
      {showQualText ? (
        <span>{qualText}</span>
      ) : (
        <span>
          {fmtMoney(sc.value)}
          {sc.valueSource === "qualitative" && (
            <Box
              component="span"
              sx={{
                ml: 0.5,
                px: 0.5,
                py: 0.1,
                fontSize: "0.6rem",
                fontWeight: 700,
                color: "#7b1fa2",
                bgcolor: "#f3e5f5",
                borderRadius: 0.5,
              }}
            >
              QUAL
            </Box>
          )}
        </span>
      )}
      <Tooltip title={tip} arrow placement="top">
        <InfoOutlinedIcon sx={{ fontSize: 14, color: "#9e9e9e", cursor: "help" }} />
      </Tooltip>
    </Box>
  );
}

function colorFor(lbl) {
  if (/worst/i.test(lbl)) return "#b71c1c";
  if (/best/i.test(lbl)) return "#2e7d32";
  return "#374151";
}

export default function RiskSummaryPanel({ risk, isLikelihoodMode = false }) {
  if (!risk) return null;
  const rows = risk.scenarioRows || [];
  const prox = typeof risk.signpostProximity === "number" ? risk.signpostProximity : 0.5;
  // ID214 — the Trending row shows the POSITION % toward the nearer pole (consistent with the gauge),
  // not a $ estimate. A ball at 0.74 reads "74% to best case".
  const pctBest = Math.round(prox * 100);
  const posLabel = prox >= 0.5 ? `${pctBest}% to best case` : `${100 - pctBest}% to worst case`;
  const overallVel = rows[0]?.velocity || risk.velocity || "";
  const best = rows[0];
  const worst = rows[1];
  const extra = rows.slice(2);

  // ID172 — in likelihood mode plausibility is a single overall likelihood band (no per-scenario
  // velocity), so the per-scenario "Velocity" column is dropped.

  const scenarioRow = (sc, k) => (
    <TableRow key={k}>
      <TableCell sx={{ ...TD, fontWeight: 700, color: colorFor(sc.label) }}>{sc.label}</TableCell>
      <TableCell sx={TD}>
        <ValueCell sc={sc} />
      </TableCell>
      <TableCell sx={TD}>{sc.preparedness || "—"}</TableCell>
      {!isLikelihoodMode && <TableCell sx={TD}>{sc.velocity || "—"}</TableCell>}
    </TableRow>
  );

  let alertLabel = "Signpost approaching threshold";
  if (risk.signpostDirection === "triggered") alertLabel = "Signpost threshold breached";
  else if (risk.signpostDirection === "imminent") alertLabel = "Signpost breach imminent";

  return (
    <Box>
      {/* ID196 — breadcrumb chips removed entirely (category & sub-category show once in the
          read-only detail block at the bottom; likelihood shows in the Current-state gauge caption;
          the time-horizon chip was also removed on request). The panel now opens straight into the
          alert banner + scenario table. */}

      {/* Signpost-alert banner */}
      {risk.signpostAlert && risk.signpostAlert !== "none" && (
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 0.75,
            mb: 1.5,
            px: 1.5,
            py: 0.75,
            borderRadius: 1,
            bgcolor: risk.signpostAlert === "red" ? "#ffebee" : "#fff8e1",
          }}
        >
          <Typography sx={{ fontSize: "1rem" }}>⚠</Typography>
          <Typography
            variant="caption"
            fontWeight={700}
            sx={{ color: risk.signpostAlert === "red" ? "#b71c1c" : "#f57f17" }}
          >
            {alertLabel}
          </Typography>
        </Box>
      )}

      {/* ID196 — risk description removed from this panel: it was duplicated with the DESCRIPTION
          field in the read-only detail block below. */}

      {/* Best / Trending / Worst table */}
      <Table size="small">
        <TableHead>
          <TableRow sx={{ bgcolor: "rgba(82,0,155,0.04)" }}>
            <TableCell sx={TH}>Scenario</TableCell>
            <TableCell sx={TH}>Consequence Levels</TableCell>
            <TableCell sx={TH}>Preparedness</TableCell>
            {!isLikelihoodMode && <TableCell sx={TH}>Velocity</TableCell>}
          </TableRow>
        </TableHead>
        <TableBody>
          {best && scenarioRow(best, "sc-best")}
          <TableRow key="trending">
            <TableCell sx={{ ...TD, fontWeight: 700, color: "#1565c0" }}>Current State</TableCell>
            <TableCell
              sx={TD}
              title="Where the risk is currently sitting between the Best and Worst limits, from the live signpost position"
            >
              {posLabel}
            </TableCell>
            <TableCell sx={TD}>—</TableCell>
            {!isLikelihoodMode && <TableCell sx={TD}>{overallVel || "—"}</TableCell>}
          </TableRow>
          {worst && scenarioRow(worst, "sc-worst")}
          {extra.map((sc, i) => scenarioRow(sc, `sc-extra-${i}`))}
        </TableBody>
      </Table>
    </Box>
  );
}
